import constate from "constate";
import { useState, useCallback } from "react";
import { useDataBufferContext } from "./useDataBuffer.js";
var BulkRowSelectionType = /* @__PURE__ */ ((BulkRowSelectionType2) => {
  BulkRowSelectionType2["ALL"] = "all";
  BulkRowSelectionType2["NONE"] = "none";
  return BulkRowSelectionType2;
})(BulkRowSelectionType || {});
const DEFAULT_SELECTION = {
  type: "none",
  exceptions: {}
};
const useBulkRowSelectionContext = ({
  onBulkRowSelectionChange
}) => {
  const { buffer } = useDataBufferContext();
  const [selection, setSelection] = useState(DEFAULT_SELECTION);
  const [selectionAnchorIndex, setSelectionAnchorIndex] = useState(null);
  const setSelectionState = useCallback(
    (state) => {
      setSelection(state);
      if (onBulkRowSelectionChange) {
        onBulkRowSelectionChange(state);
      }
    },
    [selection, setSelection, onBulkRowSelectionChange]
  );
  const getFilteredExceptions = () => {
    return Object.values(selection.exceptions).filter((value) => !!value);
  };
  const getFormattedSelection = useCallback(() => {
    const exceptions = getFilteredExceptions();
    return {
      type: selection.type,
      exceptions: exceptions.map((row) => (row == null ? void 0 : row.id) || "").filter((rowId) => !!rowId),
      exceptionsData: exceptions
    };
  }, [selection]);
  const tableSelectionStatus = () => {
    const exceptions = getFilteredExceptions();
    if (exceptions.length)
      return "some";
    return selection.type;
  };
  const getIsBulkSelecting = () => {
    const status = tableSelectionStatus();
    return status === "all" || status === "some";
  };
  const exitSelectionState = useCallback(
    (isSelecting) => {
      if (isSelecting || getIsBulkSelecting()) {
        setSelectionState(DEFAULT_SELECTION);
      }
    },
    [setSelectionState, getIsBulkSelecting]
  );
  const toggleAllRows = useCallback(() => {
    const status = tableSelectionStatus();
    setSelectionState({
      type: status === "all" || status === "some" ? "none" : "all",
      exceptions: {}
    });
  }, [selection]);
  const toggleSingleRow = useCallback(
    (totalRowCount, row, selectionMode) => {
      if (!row)
        return;
      const exceptions = getFilteredExceptions();
      if (exceptions.length === totalRowCount - 1) {
        setSelectionState({
          type: selection.type === "all" ? "none" : "all",
          exceptions: {}
        });
      } else {
        const exception = selection.exceptions[row.position];
        setSelectionAnchorIndex(row.position);
        setSelectionState({
          ...selection,
          exceptions: {
            ...selectionMode === "bulk" ? selection.exceptions : [],
            [row.position]: exception ? void 0 : row
          }
        });
      }
    },
    [selection, getFilteredExceptions]
  );
  const toggleUntil = useCallback(
    async (totalRowCount, row, selectionMode) => {
      if (!row)
        return;
      let rows = [];
      if (Object.keys(selection).length === 0) {
        toggleSingleRow(totalRowCount, row, selectionMode);
      } else {
        try {
          if (selectionAnchorIndex) {
            if (row.position < selectionAnchorIndex) {
              rows = await buffer.getRows(row.position, selectionAnchorIndex);
            } else {
              rows = await buffer.getRows(selectionAnchorIndex, row.position);
            }
            setSelectionState({
              type: "none",
              exceptions: rows.reduce((acc, row2) => {
                acc[row2.position] = row2;
                return acc;
              }, {})
            });
          } else {
            const selectionKeys = Object.keys(selection.exceptions).map(Number);
            const lastSelectionIndex = Math.max(...selectionKeys);
            const firstSelectionIndex = Math.min(...selectionKeys);
            if (row.position < firstSelectionIndex) {
              rows = await buffer.getRows(row.position, lastSelectionIndex);
            } else {
              rows = await buffer.getRows(lastSelectionIndex, row.position);
            }
            setSelectionState({
              type: "none",
              exceptions: rows.reduce((acc, row2) => {
                acc[row2.position] = row2;
                return acc;
              }, selection.exceptions)
            });
          }
        } catch (error) {
          console.error(error);
          throw new Error("Row selection is limited to 25,000 rows.");
        }
      }
    },
    [selection, toggleSingleRow]
  );
  const rowIsSelected = useCallback(
    (row) => {
      if (!row)
        return false;
      const exception = selection.exceptions[row.position];
      if (selection.type === "all") {
        return !exception;
      }
      return !!exception;
    },
    [selection]
  );
  return {
    selection,
    toggleAllRows,
    toggleSingleRow,
    toggleUntil,
    rowIsSelected,
    tableSelectionStatus,
    exitSelectionState,
    getFormattedSelection,
    getIsBulkSelecting,
    setSelectionAnchorIndex
  };
};
const [BulkRowSelectionProvider, useBulkRowSelection] = constate(
  useBulkRowSelectionContext
);
export {
  BulkRowSelectionProvider,
  BulkRowSelectionType,
  useBulkRowSelection,
  useBulkRowSelectionContext
};
